import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ROUTES, URN_QDOS_S3 } from '../../helpers/constans';
import { shareActions } from '../../redux/slices/share.slice';
import { RootState } from '../../redux/store';

const FanShare = (): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  const shareState = useSelector((state: RootState) => state.share);

  const fanShare = useMemo(() => {
    if (!Array.isArray(shareState?.fanShare)) return [];
    return shareState.fanShare;
  }, [shareState?.fanShare]);

  const fanShareHistory = useMemo(() => {
    return shareState?.fanShareHistory || { currentEvent: null, previousEvents: [] };
  }, [shareState?.fanShareHistory]);

  useEffect(() => {
    dispatch(shareActions.getFanShareHistory());
  }, [dispatch]);

  useEffect(() => {
    if (eventId) {
      dispatch(shareActions.getFanShare(eventId));
      return;
    }

    if (fanShareHistory?.currentEvent?.qdosEventId) {
      navigate(
        generatePath(ROUTES.fanShare, {
          eventId: fanShareHistory.currentEvent.qdosEventId,
        }),
        { replace: true },
      );
    }
  }, [dispatch, eventId, navigate, fanShareHistory]);

  const getMediaUrl = (value?: string): string => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${URN_QDOS_S3}${value}`;
  };

  const requestFullscreen = (element: HTMLElement): void => {
    const anyElement = element as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
      msRequestFullscreen?: () => Promise<void> | void;
    };

    if (anyElement.requestFullscreen) {
      void anyElement.requestFullscreen();
      return;
    }

    if (anyElement.webkitRequestFullscreen) {
      void anyElement.webkitRequestFullscreen();
      return;
    }

    if (anyElement.msRequestFullscreen) {
      void anyElement.msRequestFullscreen();
    }
  };

  const renderMedia = (item: any, enlarged = false): ReactElement => {
    const imageUrl = getMediaUrl(item?.imageId);
    const videoUrl = getMediaUrl(item?.videoUrl);

    if (videoUrl) {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#000',
          }}
        >
          <video
            controls
            style={{
              display: 'block',
              width: '100%',
              maxHeight: enlarged ? '75vh' : '260px',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          >
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    if (imageUrl) {
      return (
        <Box>
          <Box
            component="img"
            src={imageUrl}
            alt={item?.eventTitle || 'Fan Share media'}
            onClick={
              enlarged
                ? (event: React.MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                    requestFullscreen(event.currentTarget);
                  }
                : undefined
            }
            sx={{
              width: '100%',
              height: enlarged ? 'auto' : '220px',
              maxHeight: enlarged ? '75vh' : '220px',
              objectFit: 'cover',
              borderRadius: '8px',
              display: 'block',
              backgroundColor: '#f5f5f5',
              cursor: enlarged ? 'zoom-in' : 'default',
            }}
          />
          {enlarged && (
            <Typography sx={{ mt: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
              Click image for full screen
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: enlarged ? '260px' : '220px',
          borderRadius: '8px',
          border: '1px dashed #bbb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '1rem',
          color: '#777',
          textAlign: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography>{item?.alternativeId || 'No media preview available'}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      <Box
        sx={{
          width: 240,
          borderRight: '1px solid #e0e0e0',
          p: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <Typography variant="h5" sx={{ mb: '0.5rem' }}>
          Fan Share
        </Typography>

        {fanShareHistory?.currentEvent && (
          <Button
            variant="contained"
            onClick={() =>
              navigate(
                generatePath(ROUTES.fanShare, {
                  eventId: fanShareHistory.currentEvent.qdosEventId,
                }),
              )
            }
          >
            Current Event
          </Button>
        )}

        <Typography variant="subtitle1" sx={{ mt: '1rem' }}>
          Previous Events
        </Typography>

        {(fanShareHistory?.previousEvents || []).map((event: any) => (
          <Button
            key={event.eventId}
            variant="text"
            onClick={() =>
              navigate(
                generatePath(ROUTES.fanShare, {
                  eventId: event.qdosEventId,
                }),
              )
            }
            sx={{ justifyContent: 'flex-start' }}
          >
            {event.eventTitle}
          </Button>
        ))}
      </Box>

      <Box sx={{ flex: 1, p: '2rem' }}>
        <Typography variant="h4" sx={{ mb: '1.5rem' }}>
          Fan Share
        </Typography>

        {!fanShare.length ? (
          <Typography sx={{ color: '#666' }}>No Fan Share media found for this event.</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: '1rem',
              gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))',
            }}
          >
            {fanShare.map((item: any) => (
              <Box
                key={item._id}
                onClick={() => setSelectedItem(item)}
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: '12px',
                  p: '1rem',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: '0.5rem' }}>
                  {item.eventTitle || 'Fan Share item'}
                </Typography>

                <Typography sx={{ mb: '0.75rem' }}>
                  {item.description || 'No description'}
                </Typography>

                <Box sx={{ mb: '0.75rem' }}>{renderMedia(item)}</Box>

                <Typography sx={{ mb: '0.75rem' }}>❤️ {item.likeCount || 0}</Typography>

                {item.canRemove && (
                  <Button
                    size="small"
                    color="error"
                    onClick={(event) => {
                      event.stopPropagation();
                      dispatch(shareActions.removeShareItem(item._id));
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '1rem',
            pt: '1rem',
          }}
        >
          <Typography variant="h6">
            {selectedItem?.eventTitle || 'Fan Share item'}
          </Typography>

          <IconButton onClick={() => setSelectedItem(null)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          {selectedItem && (
            <Box>
              <Typography sx={{ mb: '1rem' }}>
                {selectedItem.description || 'No description'}
              </Typography>

              <Box sx={{ mb: '1rem' }}>{renderMedia(selectedItem, true)}</Box>

              <Typography>❤️ {selectedItem.likeCount || 0}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FanShare;